import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import styled from '@emotion/styled';

import { EliceButton } from '../button';
import { textPrimaryColor, textSecondaryColor } from '../tokenColorTemporary';

const DialogTitleText = styled(Typography)({
  fontSize: '1.375rem',
  fontWeight: 700,
  color: textPrimaryColor,
});

const DialogBodyText = styled(Typography)({
  fontSize: '1.375rem',
  color: textSecondaryColor,
});

import type { DialogProps } from '@mui/material';

export interface EliceDialogProps extends Omit<DialogProps, 'open' | 'onClose' | 'title' | 'content'> {
  open?: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  content?: React.ReactNode;
  children?: React.ReactNode;
  // Legacy props for backward compatibility
  anchorElement?: React.ReactNode;
  type?: 'YesOrNo' | 'OK';
  onConfirm?: VoidFunction;
  onAgree?: VoidFunction;
  onDisagree?: VoidFunction;
}

const EliceDialog = ({
  open: controlledOpen,
  onClose: controlledOnClose,
  title,
  content,
  children,
  anchorElement,
  type = 'OK',
  onConfirm,
  onAgree,
  onDisagree,
  sx,
  ...dialogProps
}: EliceDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled open if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const handleClose = isControlled ? controlledOnClose : () => setInternalOpen(false);

  // Generate IDs for accessibility
  const titleId = React.useId();
  const descriptionId = React.useId();

  const dialogContent = (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={content ? descriptionId : undefined}
      sx={{
        borderRadius: '1rem',
        '& .MuiDialog-paper': {
          borderRadius: '1rem',
        },
        ...sx,
      }}
      PaperProps={{
        style: {
          minWidth: '40rem',
          padding: '1.25rem 1.5rem',
          gap: '1rem',
        },
      }}
      {...dialogProps}
    >
      {title && (
        <DialogTitle id={titleId}>
          {typeof title === 'string' ? (
            <DialogTitleText variant="subtitle1">
              {title}
            </DialogTitleText>
          ) : (
            title
          )}
        </DialogTitle>
      )}
      {content && (
        <DialogContent>
          <DialogContentText id={descriptionId}>
            {typeof content === 'string' ? (
              <DialogBodyText variant="body1">
                {content}
              </DialogBodyText>
            ) : (
              content
            )}
          </DialogContentText>
        </DialogContent>
      )}
      {children ? (
        <DialogActions>
          {children}
        </DialogActions>
      ) : (
        <DialogActions>
          {type === 'OK' && (
            <EliceButton
              textColor='#fff'
              onClick={() => {
                if (onConfirm) {
                  onConfirm();
                }
                handleClose?.();
              }}
            >
              닫기
            </EliceButton>
          )}
          {type === 'YesOrNo' && (
            <>
              <EliceButton
                variant="outlined"
                onClick={() => {
                  if (onDisagree) {
                    onDisagree();
                  }
                  handleClose?.();
                }}
              >
                취소
              </EliceButton>
              <EliceButton
                variant="contained"
                textColor='#fff'
                onClick={() => {
                  if (onAgree) {
                    onAgree();
                  }
                  handleClose?.();
                }}
              >
                확인
              </EliceButton>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );

  // If anchorElement is provided, use legacy behavior
  if (anchorElement) {
    return (
      <>
        <Stack onClick={() => setInternalOpen(true)}>{anchorElement}</Stack>
        {dialogContent}
      </>
    );
  }

  return dialogContent;
};

export default EliceDialog;
